import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Grid, IconButton, CircularProgress, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import moment, { Moment } from 'moment';
import {
  fetchUsersStatisticsByDays,
  selectUsersStatisticsByDays
} from '../../../redux/slices/balanceSlice';
import { selectCities } from '../../../redux/slices/citiesSlice';
import { getActiveContractsAsync } from '../../../redux/slices/contractsSlice';
import { selectAuth } from '../../../redux/slices/authSlice';
import { selectAdmins } from '../../../redux/slices/adminsSlice';
import { selectScooters } from '../../../redux/slices/scootersSlice';
import usePermissions from '../../../hooks/usePermissions';
import { useWebSocketContext } from '../../../containers/WebSocketProvider/WebSocketProvider';
import { 
  DirectionsCar, 
  AccountBalanceWallet, 
  TrendingUp, 
  CalendarToday, 
  DirectionsBike,
  Battery20,
  ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material';
import { TripState } from '../types';
import axios from '../../../resources/axios';

const TodayStatistic = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const checkPermission = usePermissions();
  const { sendSocketMessage } = useWebSocketContext();
  
  const usersStatisticsByDays = useSelector(selectUsersStatisticsByDays);
  const cities = useSelector(selectCities);
  const auth = useSelector(selectAuth);
  const adminInfo = useSelector(selectAdmins);
  const { onContract, lowBattery } = useSelector(selectScooters);

  const [tripsCount, setTripsCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Moment>(moment());
  const [incomeSum, setIncomeSum] = useState(0);
  const [expenseSum, setExpenseSum] = useState(0);
  
  const [tripsLoading, setTripsLoading] = useState(false);
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const averageTripCost = useMemo(() => {
    if (tripsCount === 0) return 0;
    return incomeSum / tripsCount;
  }, [incomeSum, tripsCount]);

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const currentCityId = useMemo(() => {
    return adminInfo.currentCity;
  }, [adminInfo.currentCity]);

  const dateRange = useMemo(() => {
    return {
      start: moment(selectedDate).startOf('day'),
      end: moment(selectedDate).endOf('day')
    };
  }, [selectedDate]);

  const isToday = useMemo(() => {
    return moment(selectedDate).isSame(moment(), 'day');
  }, [selectedDate]);

  const handlePreviousDay = () => {
    setSelectedDate(prev => moment(prev).subtract(1, 'day'));
  };

  const handleNextDay = () => {
    setSelectedDate(prev => moment(prev).add(1, 'day'));
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = moment(event.target.value, 'YYYY-MM-DD');
    if (newDate.isValid()) {
      setSelectedDate(newDate);
    }
  };

  useEffect(() => {
    if (checkPermission('contracts') && currentCityId) {
      const fetchTripsData = async () => {
        setTripsLoading(true);
        try {
          const result: any = await dispatch(
            getActiveContractsAsync({
              active: false,
              from: dateRange.start.unix() * 1000,
              to: dateRange.end.unix() * 1000,
              cityId: currentCityId
            })
          );
          setTripsCount(result?.contractsCount ?? 0);
        } finally {
          setTripsLoading(false);
        }
      };

      fetchTripsData();
    }
  }, [checkPermission, dispatch, currentCityId, dateRange]);

  useEffect(() => {
    if (checkPermission('statistics') && currentCityId) {
      const fetchIncomeData = async () => {
        setIncomeLoading(true);
        try {
          const response = await axios.get('/balance/cities', {
            params: {
              from: moment(dateRange.start).subtract(1, 'millisecond').toISOString(),
              to: moment(dateRange.end).add(1, 'day').startOf('day').toISOString(),
              cityId: currentCityId
            }
          });
          const sum = (response.data || []).reduce((acc: number, item: any) => acc + (item.count || 0), 0);
          setIncomeSum(sum);
        } catch (error) {
          console.error('Error fetching income data:', error);
          setIncomeSum(0);
        } finally {
          setIncomeLoading(false);
        }
      };

      fetchIncomeData();
    }
  }, [checkPermission, currentCityId, dateRange]);

  useEffect(() => {
    if (checkPermission('statistics') && currentCityId) {
      const fetchExpenseData = async () => {
        setExpenseLoading(true);
        try {
          const response = await axios.get('/statistics', {
            params: {
              dateFrom: dateRange.start.toISOString(),
              dateTo: moment(dateRange.end).add(1, 'day').startOf('day').toISOString(),
              cityId: currentCityId
            }
          });
          const sum = (response.data || []).reduce((acc: number, item: any) => acc + (item.count || 0), 0);
          setExpenseSum(sum);
        } catch (error) {
          console.error('Error fetching expense data:', error);
          setExpenseSum(0);
        } finally {
          setExpenseLoading(false);
        }
      };

      fetchExpenseData();
    }
  }, [checkPermission, currentCityId, dateRange]);

  useEffect(() => {
    if (!isSuperAdmin && !currentCityId) return;
    
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        await dispatch(fetchUsersStatisticsByDays());
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [dispatch, isSuperAdmin, currentCityId]);

  // Load scooter counters (onContract, lowBattery) via WebSocket
  useEffect(() => {
    if (currentCityId && sendSocketMessage && checkPermission('scooters')) {
      sendSocketMessage('scooters', { cityId: currentCityId });
      
      // Refresh counters every 30 seconds
      const interval = setInterval(() => {
        sendSocketMessage('scooters', { cityId: currentCityId });
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [currentCityId, sendSocketMessage, checkPermission]);

  const hexToRgba = (hex: string, alpha: number = 1): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const StatCard = ({ icon, label, value, color, loading = false }: { icon: ReactNode, label: string, value: number | string, color: string, loading?: boolean }) => {
    const displayValue = typeof value === 'number' 
      ? value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : value;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '14px 16px',
          borderRadius: '12px',
          transition: 'all 0.2s ease',
          backgroundColor: hexToRgba(color, 0.12),
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          minWidth: 160,
          minHeight: 80,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              backgroundColor: hexToRgba(color, 0.18),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': {
                fontSize: 24,
                color: color
              }
            }}
          >
            {icon}
          </Box>
          {loading ? (
            <CircularProgress size={32} sx={{ color: color }} />
          ) : (
            <Typography
              sx={{
                fontSize: 32,
                fontWeight: 700,
                color: color,
                lineHeight: 1
              }}
            >
              {displayValue}
            </Typography>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            textAlign: 'center'
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        padding: '20px',
        border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
          }}
        >
          {isToday ? t('stats.statsForToday') : t('stats.statsForDate', { date: selectedDate.format('DD.MM.YYYY') })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={handlePreviousDay}
            size="small"
            sx={{
              backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A'
              }
            }}
          >
            <ArrowBackIos sx={{ fontSize: 16, ml: 0.5 }} />
          </IconButton>
          <TextField
            type="date"
            value={selectedDate.format('YYYY-MM-DD')}
            onChange={handleDateChange}
            size="small"
            InputProps={{
              sx: {
                fontSize: '0.875rem',
                backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
                borderRadius: '12px',
                fontWeight: 500,
                minWidth: 140,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '& input': {
                  textAlign: 'center',
                  padding: '6px 16px',
                  color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E'
                }
              }
            }}
          />
          <IconButton
            onClick={handleNextDay}
            disabled={isToday}
            size="small"
            sx={{
              backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A'
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C',
                opacity: 0.4
              }
            }}
          >
            <ArrowForwardIos sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2} justifyContent="center" sx={{ mx: 0, width: '100%' }}>
        <Grid item xs={6} sm={6} md={4}>
          <StatCard
            icon={<DirectionsCar />}
            label={t('stats.trips')}
            value={tripsCount}
            color='#2196F3'
            loading={tripsLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4}>
          <StatCard
            icon={<AccountBalanceWallet />}
            label={t('stats.topUp')}
            value={incomeSum}
            color='#4CAF50'
            loading={incomeLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4}>
          <StatCard
            icon={<TrendingUp />}
            label={t('stats.avgTrip')}
            value={averageTripCost.toFixed(2)}
            color='#FF9800'
            loading={tripsLoading || incomeLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4}>
          <StatCard
            icon={<CalendarToday />}
            label={t('stats.newUsersPerDay')}
            value={usersStatisticsByDays?.lastDay ?? 0}
            color='#9C27B0'
            loading={usersLoading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4}>
          <StatCard
            icon={<DirectionsBike />}
            label={t('stats.inTrip')}
            value={onContract ?? 0}
            color='#F44336'
            loading={false}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4}>
          <StatCard
            icon={<Battery20 />}
            label={t('stats.discharged')}
            value={lowBattery ?? 0}
            color={theme.palette.mode === 'light' ? '#000000' : '#9E9E9E'}
            loading={false}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TodayStatistic;
